/**
 * Two Column Accordion Carousel Block
 *
 * Syncs accordion panels with the image carousel (Swiper).
 * Base Swiper layout CSS comes from vooruitzicht-theme-core (vtc-carousel).
 */

import Swiper from 'swiper';
import { Pagination } from 'swiper/modules';

const INIT_ATTR = 'data-accordion-carousel-initialized';

/**
 * @param {HTMLElement} accordionElement
 */
function initBlock(accordionElement) {
	if (accordionElement.hasAttribute(INIT_ATTR)) {
		return;
	}

	accordionElement.setAttribute(INIT_ATTR, 'true');

	const blockId = accordionElement.dataset.carouselSync;
	const carouselElement = blockId
		? document.querySelector(`.btcac-carousel-swiper[data-carousel-id="${blockId}"]`)
		: null;

	let swiper = null;

	if (carouselElement && !carouselElement.hasAttribute('data-carousel-initialized')) {
		swiper = new Swiper(carouselElement, {
			modules: [Pagination],
			slidesPerView: 1,
			spaceBetween: 0,
			grabCursor: false,
			allowTouchMove: false,
			pagination: {
				el: carouselElement.querySelector('.swiper-pagination'),
				clickable: true,
				bulletClass: 'btcac-pagination-bullet',
				bulletActiveClass: 'btcac-pagination-bullet--active',
			},
			a11y: {
				enabled: true,
				prevSlideMessage: 'Previous slide',
				nextSlideMessage: 'Next slide',
			},
		});

		carouselElement.setAttribute('data-carousel-initialized', 'true');
		syncPaginationToAccordion(carouselElement, accordionElement, swiper);
	}

	initAccordionWithSync(accordionElement, swiper);
}

/**
 * @param {HTMLElement} accordionElement
 * @param {import('swiper').Swiper | null} swiper
 */
function initAccordionWithSync(accordionElement, swiper) {
	const items = Array.from(accordionElement.querySelectorAll('[data-accordion-item]'));

	items.forEach((item, index) => {
		const trigger = item.querySelector('[data-accordion-trigger]');
		const content = item.querySelector('[data-accordion-content]');

		if (!trigger || !content) {
			return;
		}

		const itemId = `accordion-item-${Date.now()}-${index}`;
		const triggerId = `${itemId}-trigger`;
		const contentId = `${itemId}-content`;

		trigger.setAttribute('id', triggerId);
		trigger.setAttribute('aria-controls', contentId);
		content.setAttribute('id', contentId);
		content.setAttribute('role', 'region');
		content.setAttribute('aria-labelledby', triggerId);

		trigger.addEventListener('click', (event) => {
			event.preventDefault();

			const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

			items.forEach((otherItem) => {
				const otherTrigger = otherItem.querySelector('[data-accordion-trigger]');
				const otherContent = otherItem.querySelector('[data-accordion-content]');

				if (otherTrigger && otherContent) {
					otherTrigger.setAttribute('aria-expanded', 'false');
					otherContent.removeAttribute('data-accordion-open');
				}
			});

			if (!isExpanded) {
				trigger.setAttribute('aria-expanded', 'true');
				content.setAttribute('data-accordion-open', '');

				if (swiper) {
					const slideIndex = parseInt(item.dataset.slideIndex, 10);
					if (!Number.isNaN(slideIndex)) {
						swiper.slideTo(slideIndex);
					}
				}
			}
		});
	});
}

/**
 * @param {HTMLElement} carouselElement
 * @param {HTMLElement} accordionElement
 * @param {import('swiper').Swiper} swiper
 */
function syncPaginationToAccordion(carouselElement, accordionElement, swiper) {
	const pagination = carouselElement.querySelector('.swiper-pagination');

	if (!pagination) {
		return;
	}

	swiper.on('slideChange', () => {
		const activeIndex = swiper.activeIndex;
		const accordionItems = accordionElement.querySelectorAll('[data-accordion-item]');

		accordionItems.forEach((item, index) => {
			const trigger = item.querySelector('[data-accordion-trigger]');
			const content = item.querySelector('[data-accordion-content]');

			if (!trigger || !content) {
				return;
			}

			if (index === activeIndex) {
				trigger.setAttribute('aria-expanded', 'true');
				content.setAttribute('data-accordion-open', '');
			} else {
				trigger.setAttribute('aria-expanded', 'false');
				content.removeAttribute('data-accordion-open');
			}
		});
	});
}

function initTwoColumnAccordionCarousels() {
	document.querySelectorAll('[data-carousel-sync]').forEach(initBlock);
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initTwoColumnAccordionCarousels);
} else {
	initTwoColumnAccordionCarousels();
}

document.addEventListener('brixx:contentLoaded', initTwoColumnAccordionCarousels);

export { initTwoColumnAccordionCarousels, initBlock };
